/* eslint-disable max-len */
/**
 * Awvent - Users API
 *
 * Hand-authored client for User.Api/TeamController (codegen parity with the
 * other shared/apis services). GAP-ADMIN-PTG-002 — the audience picker lists
 * Teams via teamGet; full CRUD lands with the Teams & Groups page (GAP-ADMIN-PTG-004).
 */
import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

import { TeamDtoListWebApiResponse } from '../model/teamDtoListWebApiResponse';
import { TeamDto } from '../model/teamDto';

import { Configuration } from '../../../../configuration';
import { BASE_PATH } from '../../../../variables';

// Wire shapes match the backend command DTOs (User.Contracts/Commands/TeamCommands.cs).
// SPEC-web-planner-013 AC-62 / SPEC-web-planner-014 AC-5, AC-9: a team names its
// territory by id and may have a lead; membership is User.TeamId, so no memberIds.
export interface TeamCreateCommand {
    team: {
        id?: string;
        name: string;
        territoryId?: string;
        leadUserId?: string;
    };
}

/** On update an absent territoryId/leadUserId keeps the value; clearTerritory/clearLead remove it. */
export interface TeamUpdateCommand {
    team: {
        id: string;
        name: string;
        territoryId?: string;
        leadUserId?: string;
        clearTerritory?: boolean;
        clearLead?: boolean;
    };
}

/** SPEC-web-planner-014 AC-22/AC-28 (SPEC-web-planner-013 AC-91): PUT /api/v1/Team/{id}/Members. */
export interface TeamMembersBody {
    userIds: string[];
}
export type TeamMemberAction = 'added' | 'moved' | 'unchanged' | 'removed' | 'notApplied';
export interface TeamMembersResult {
    teamId: string;
    results: Array<{ userId: string; action: TeamMemberAction; fromTeamId?: string | null }>;
    leadsCleared: string[];
}

export interface TeamDtoWebApiResponse {
    isSuccess?: boolean;
    message?: string;
    result?: TeamDto;
    erros?: Array<{ key?: string; description?: string }>;
}

@Injectable()
export class TeamService {

    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();

    protected basePath = environment.api.users;

    constructor(protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    private buildHeaders(xTenantId?: string, withContentType = false): HttpHeaders {
        let headers = this.defaultHeaders;
        if (xTenantId) {
            headers = headers.set('x-tenant-id', String(xTenantId));
        }
        if (this.configuration.accessToken) {
            const accessToken = typeof this.configuration.accessToken === 'function'
                ? this.configuration.accessToken()
                : this.configuration.accessToken;
            headers = headers.set('Authorization', 'Bearer ' + accessToken);
        }
        const accepts = ['text/plain', 'application/json', 'text/json'];
        const accept = this.configuration.selectHeaderAccept(accepts);
        if (accept !== undefined) {
            headers = headers.set('Accept', accept);
        }
        if (withContentType) {
            const ct = this.configuration.selectHeaderContentType(['application/json', 'text/json', 'application/_*+json']);
            if (ct !== undefined) {
                headers = headers.set('Content-Type', ct);
            }
        }
        return headers;
    }

    /**
     * List teams in the current workspace.
     */
    public teamGet(xTenantId: string, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling teamGet.');
        }
        return this.httpClient.request<TeamDtoListWebApiResponse>('get', `${this.basePath}/api/v1/Team`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Create a team. POST /api/v1/Team — body matches TeamCreateCommand.
     */
    public teamPost(xTenantId: string, body: TeamCreateCommand, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling teamPost.');
        }
        return this.httpClient.request<TeamDtoWebApiResponse>('post', `${this.basePath}/api/v1/Team`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId, true),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Update a team. PUT /api/v1/Team — body matches TeamUpdateCommand.
     */
    public teamPut(xTenantId: string, body: TeamUpdateCommand, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling teamPut.');
        }
        return this.httpClient.request<TeamDtoWebApiResponse>('put', `${this.basePath}/api/v1/Team`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId, true),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Set a team's members in one call. PUT /api/v1/Team/{id}/Members — SPEC-web-planner-014 Phase 6:
     * listed users join (or move to) the team, current members not listed leave it.
     */
    public teamMembersPut(xTenantId: string, teamId: string, body: TeamMembersBody, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling teamMembersPut.');
        }
        if (teamId === null || teamId === undefined) {
            throw new Error('Required parameter teamId was null or undefined when calling teamMembersPut.');
        }
        return this.httpClient.request<{ isSuccess?: boolean; message?: string; result?: TeamMembersResult }>('put',
            `${this.basePath}/api/v1/Team/${encodeURIComponent(String(teamId))}/Members`,
            {
                body,
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId, true),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }

    /**
     * Delete a team. DELETE /api/v1/Team/{id} - SPEC-web-planner-029 AC-B1 (backend Phase A): the team's
     * members are left with no team; an unknown id answers HTTP 400 with code 404.
     */
    public teamDelete(xTenantId: string, id: string, observe: any = 'body', reportProgress: boolean = false): Observable<any> {
        if (xTenantId === null || xTenantId === undefined) {
            throw new Error('Required parameter xTenantId was null or undefined when calling teamDelete.');
        }
        if (!id) {
            throw new Error('Required parameter id was null or undefined when calling teamDelete.');
        }
        return this.httpClient.request<{ isSuccess?: boolean; message?: string; result?: boolean }>('delete',
            `${this.basePath}/api/v1/Team/${encodeURIComponent(String(id))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: this.buildHeaders(xTenantId),
                observe: observe,
                reportProgress: reportProgress,
            }
        );
    }
}
